import React, { createContext, useContext, useState, useEffect } from 'react';
import {
  apiListCoaches,
  apiCreateCoach,
  apiUpdateCoach,
} from '../api/coachesApi';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Helper to safely normalise API response to an array
const normaliseCoachesResponse = (raw) => {
  // Handle the most common shapes gracefully.
  if (Array.isArray(raw)) {
    return raw;
  }

  // If NoCodeBackend returns an object with a property holding the rows,
  // try common keys: data, items, records.
  if (raw && typeof raw === 'object') {
    if (Array.isArray(raw.data)) return raw.data;
    if (Array.isArray(raw.items)) return raw.items;
    if (Array.isArray(raw.records)) return raw.records;
  }

  // Fallback: return an empty array so .filter() never throws.
  return [];
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [coaches, setCoaches] = useState([]);
  const [showArchived, setShowArchived] = useState(false);

  // Load coaches on initial load
  useEffect(() => {
    const fetchCoaches = async () => {
      try {
        const raw = await apiListCoaches();

        // Debug: log the raw shape once so we can inspect it in DevTools
        // eslint-disable-next-line no-console
        console.log('[AuthProvider] apiListCoaches raw response:', raw);

        const list = normaliseCoachesResponse(raw);
        setCoaches(list);
      } catch (err) {
        console.error('Error fetching coaches via NCB:', err);
        // Fallback to mock data if API fails outright
        setCoaches([
          {
            id: 'mock1',
            username: 'coach1',
            name: 'Sarah Johnson',
            role: 'Amazon FBA/FBM Specialist',
            email: 'sarah@example.com',
            is_archived: false,
          },
          {
            id: 'mock2',
            username: 'coach2',
            name: 'Mike Rodriguez',
            role: 'Mindset/Accountability Coach',
            email: 'mike@example.com',
            is_archived: false,
          },
        ]);
      }
    };

    fetchCoaches();
  }, []);

  // Check for saved user session
  useEffect(() => {
    const savedUser = localStorage.getItem('marketing_mentor_user');
    if (savedUser) {
      setUser(JSON.parse(savedUser));
    }
    setLoading(false);
  }, []);

  // Login:
  // 1) First try admin credentials via Cloudflare env vars (ADMIN_USERNAME, ADMIN_PASSWORD, ADMIN_LOGIN)
  //    using the /api/admin-login function.
  // 2) If that fails, fall back to coach-based login via NoCodeBackend.
  const login = async (username, password) => {
    // 1) First: attempt admin login via Cloudflare Function
    try {
      const adminRes = await fetch('/api/admin-login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ username, password }),
      });

      if (adminRes.ok) {
        const adminData = await adminRes.json();
        if (adminData?.success && adminData?.user) {
          const adminUser = adminData.user;

          setUser(adminUser);
          localStorage.setItem(
            'marketing_mentor_user',
            JSON.stringify(adminUser)
          );

          // Admin login succeeded; skip coach login completely
          return true;
        }
      }
    } catch (adminErr) {
      // If the admin function fails for any reason, log and continue to normal login path.
      // eslint-disable-next-line no-console
      console.error('Admin login check failed, falling back to coach login:', adminErr);
    }

    // 2) Fallback: existing coach-based login via NoCodeBackend
    try {
      // Fetch fresh list of coaches to verify
      const raw = await apiListCoaches();

      // eslint-disable-next-line no-console
      console.log('[AuthProvider] login apiListCoaches raw response:', raw);

      const allCoaches = normaliseCoachesResponse(raw);

      // Keep AuthContext state in sync with the normalised list
      setCoaches(allCoaches);

      const coach = allCoaches.find(
        (c) => c.username === username && c.is_archived === false
      );

      // In a real app, password should be hashed. Here we compare plain text as per legacy.
      if (coach && coach.password === password) {
        const userData = { ...coach };
        // Clean up sensitive data before storing
        delete userData.password;
        setUser(userData);
        localStorage.setItem('marketing_mentor_user', JSON.stringify(userData));
        return true;
      }

      return false;
    } catch (err) {
      console.error('Error during coach login:', err);

      // Fallback mock login against whatever coaches are already in state
      const safeCoaches = Array.isArray(coaches) ? coaches : [];
      const mockCoach = safeCoaches.find(
        (c) =>
          c.username === username &&
          password === 'password123' &&
          !c.is_archived
      );

      if (mockCoach) {
        const userData = { ...mockCoach };
        setUser(userData);
        localStorage.setItem('marketing_mentor_user', JSON.stringify(userData));
        return true;
      }

      return false;
    }
  };

  const logout = () => {
    setUser(null);
    localStorage.removeItem('marketing_mentor_user');
  };

  const createCoach = async (coachData) => {
    try {
      const newCoachData = { ...coachData, is_archived: false };
      const created = await apiCreateCoach(newCoachData);
      setCoaches((prev) => [...(Array.isArray(prev) ? prev : []), created]);
      return created;
    } catch (err) {
      console.error('Error creating coach:', err);
      throw err;
    }
  };

  const updateCoach = async (coachData) => {
    try {
      const { id, ...updateData } = coachData;
      if (updateData.password === '') {
        delete updateData.password;
      }

      await apiUpdateCoach(id, updateData);

      // Refresh local state
      setCoaches((prev) => {
        const list = Array.isArray(prev) ? prev : [];
        return list.map((coach) =>
          coach.id.toString() === id.toString()
            ? { ...coach, ...updateData }
            : coach
        );
      });

      // Update current user if it matches
      if (user && user.id.toString() === id.toString()) {
        const updatedUser = { ...user, ...updateData };
        setUser(updatedUser);
        localStorage.setItem(
          'marketing_mentor_user',
          JSON.stringify(updatedUser)
        );
      }

      return { id, ...updateData };
    } catch (err) {
      console.error('Error updating coach:', err);
      throw err;
    }
  };

  const archiveCoach = async (coachId) => {
    try {
      await apiUpdateCoach(coachId, { is_archived: true });
      setCoaches((prev) => {
        const list = Array.isArray(prev) ? prev : [];
        return list.map((coach) =>
          coach.id.toString() === coachId.toString()
            ? { ...coach, is_archived: true }
            : coach
        );
      });

      if (user && user.id.toString() === coachId.toString()) {
        logout();
      }
      return true;
    } catch (err) {
      console.error('Error archiving coach:', err);
      throw err;
    }
  };

  const restoreCoach = async (coachId) => {
    try {
      await apiUpdateCoach(coachId, { is_archived: false });
      setCoaches((prev) => {
        const list = Array.isArray(prev) ? prev : [];
        return list.map((coach) =>
          coach.id.toString() === coachId.toString()
            ? { ...coach, is_archived: false }
            : coach
        );
      });
      return true;
    } catch (err) {
      console.error('Error restoring coach:', err);
      throw err;
    }
  };

  const toggleShowArchived = () => {
    setShowArchived((prev) => !prev);
  };

  const getFilteredCoaches = () => {
    const list = Array.isArray(coaches) ? coaches : [];

    return showArchived
      ? list
      : list.filter((coach) => !coach.is_archived);
  };

  const value = {
    user,
    login,
    logout,
    loading,
    coaches: getFilteredCoaches(),
    allCoaches: Array.isArray(coaches) ? coaches : [],
    showArchived,
    toggleShowArchived,
    createCoach,
    updateCoach,
    archiveCoach,
    restoreCoach,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};